import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/**
 * Which legacy system produced the file (YUKI-329). Both run the same
 * dry-run / commit contract on their own route.
 */
export type LegacyReceiptImportFormat = '1c' | 'gp';

const ROUTE: Record<LegacyReceiptImportFormat, string> = {
  '1c': '/receipts/legacy-import',
  gp: '/receipts/legacy-gp-import',
};

/**
 * YUKI-329: how long a legacy import request may run.
 *
 * The shared client gives up after 15 s, but a commit writes every receipt of
 * a store's history in one transaction the server allows five minutes for,
 * and a dry run of a large file reads the whole catalogue it names. Timing out
 * the browser does not stop the server: the operator was told the import
 * failed while it went on to succeed, and the next attempt then reported
 * every receipt as already imported.
 */
export const LEGACY_IMPORT_TIMEOUT_MS = 330_000;

/**
 * The parsed file, sent as read. A 1C cutover carries `Receipts` and
 * `ReceiptsRet`; a GP export carries `Receipts` (returns inside it) and GP's
 * own `HeaderInfo`, which the server ignores.
 */
export type LegacyReceiptImportPayload = {
  Receipts: unknown[];
  ReceiptsRet?: unknown[];
  [key: string]: unknown;
};

export type LegacyReceiptImportError = {
  sourceId: string;
  path: string;
  message: string;
};

/**
 * YUKI-332: the legal entity the store traded as in 1C. The export carries no
 * VAT; this decides the register tax every imported line is returned under.
 */
export type LegacyTradingEntity = 'individualEntrepreneur' | 'llc';

export type LegacyReceiptImportReport = {
  canCommit: boolean;
  digest: string;
  timeZone: string;
  /** 1C only (YUKI-332): the entity the lines were taxed under. Absent for GP. */
  tradingEntity?: LegacyTradingEntity;
  receiptCount: number;
  returnCount: number;
  departmentCodes: string[];
  /** Rows rejected; unlike errorCount, one receipt is counted only once. */
  rejectedReceiptCount: number;
  errorCount: number;
  errors: LegacyReceiptImportError[];
  errorsTruncated: boolean;
};

/**
 * YUKI-332 x YUKI-329: which file, and what its route needs. The trading
 * entity decides a 1C import's register tax and is required there; a GP export
 * carries its own `TaxRate` per line, and the GP routes are strict, so a GP
 * request never names one.
 */
export type LegacyReceiptImportSource =
  | { format?: '1c'; tradingEntity: LegacyTradingEntity }
  | { format: 'gp'; tradingEntity?: never };

export function useLegacyReceiptImportDryRun() {
  return useMutation({
    mutationFn: async (
      input: {
        payload: LegacyReceiptImportPayload;
        timeZone?: string;
      } & LegacyReceiptImportSource,
    ): Promise<LegacyReceiptImportReport> => {
      const params: Record<string, string> = {};
      if (input.format !== 'gp') params.tradingEntity = input.tradingEntity;
      if (input.timeZone !== undefined) params.timeZone = input.timeZone;
      const response = await apiClient.post<LegacyReceiptImportReport>(
        `${ROUTE[input.format ?? '1c']}:dry-run`,
        input.payload,
        {
          timeout: LEGACY_IMPORT_TIMEOUT_MS,
          ...(Object.keys(params).length === 0 ? {} : { params }),
        },
      );
      return response.data;
    },
  });
}

export type LegacyReceiptImportResult = {
  created: number;
  /** YUKI-39: receipts dropped by `skipErrors`, so a partial import is visible. */
  skipped: number;
  skippedIds: string[];
  /** The zone the legacy wall-clock timestamps were read in. */
  timeZone: string;
};

export function useLegacyReceiptImportCommit() {
  return useMutation({
    mutationFn: async ({
      format = '1c',
      ...body
    }: {
      payload: LegacyReceiptImportPayload;
      expectedDigest: string;
      skipErrors?: boolean;
      timeZone?: string;
    } & LegacyReceiptImportSource): Promise<LegacyReceiptImportResult> => {
      // `format` picks the route; the commit body is strict and must not carry
      // it. A 1C body carries the trading entity bound into the dry run's digest.
      const response = await apiClient.post<LegacyReceiptImportResult>(
        `${ROUTE[format]}:commit`,
        body,
        { timeout: LEGACY_IMPORT_TIMEOUT_MS },
      );
      return response.data;
    },
  });
}
